import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, Plus, Repeat } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { useDtpCartStore } from '../../store/useDtpCartStore';
import { addProductsToCart, formatMoney } from './PortalOrderParts';
import type { PortalRecommendation } from '../../types/refills';
import { track, trackOnce } from '../../lib/analytics';

/**
 * "For you" cards for the portal home: time to reorder, often added with
 * what you bought, or the clinic's featured product (rules in
 * backend services/recommendations.ts). Add to cart follows Buy again's rules.
 *
 * Quiet by design: renders nothing while loading, on error, or when there is
 * nothing to suggest, so the home page never shows an empty box.
 */
export function PortalRecommendations({ title = 'Recommended for you' }: { title?: string }) {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const recommendations = useQuery<PortalRecommendation[]>({
    queryKey: ['portal-recommendations', physicianId, patientId],
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await dtpApi.getRecommendations();
      return (res.success && res.data?.items) || [];
    },
  });
  const items = recommendations.data || [];
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="portal-recommendations">
      <h2 id="portal-recommendations" className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--portal-muted)]">{title}</h2>
      <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item: PortalRecommendation) => <li key={`${item.kind}-${item.productId}`}><RecommendationCard item={item} /></li>)}
      </ul>
    </section>
  );
}

function RecommendationCard({ item }: { item: PortalRecommendation }) {
  const { physicianId } = useDtpParams();
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const inCart = useDtpCartStore(s => s.items.some(i => i.productId === item.productId));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  // An impression, once per product per session however often Home re-renders.
  useEffect(() => {
    trackOnce(`recommendation:${item.kind}:${item.productId}`, 'recommendation_viewed', { kind: item.kind, productId: item.productId });
  }, [item.kind, item.productId]);

  async function add() {
    if (!physicianId) return;
    setBusy(true); setMessage('');
    try {
      const added = await addProductsToCart(physicianId, [item.productId], `portal_recommendation_${item.kind}`);
      if (added) track('recommendation_added_to_cart', { kind: item.kind, productId: item.productId });
      if (!added) setMessage('This can’t be added alongside what’s in your cart.');
    } catch {
      setMessage('We couldn’t add this. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="portal-card flex h-full flex-col p-5">
      <p className="text-xs font-medium text-[var(--portal-muted)]">{item.reason}</p>
      <div className="mt-2 flex items-start gap-3">
        {item.image
          ? <img src={item.image} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
          : <span aria-hidden="true" className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[var(--portal-tint-2)] font-semibold">{item.productName.trim()[0]?.toUpperCase()}</span>}
        <div className="min-w-0">
          <h3 className="font-semibold leading-snug">{item.productName}</h3>
          <p className="mt-0.5 text-sm tabular-nums text-[var(--portal-muted)]">{formatMoney(item.price)}</p>
        </div>
      </div>
      {!impersonated && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {inCart ? (
            <Link to={dtpPath(physicianId, '/checkout')} className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">
              <Check aria-hidden="true" className="h-4 w-4" />In your cart · Checkout
            </Link>
          ) : (
            <button type="button" onClick={add} disabled={busy}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-60">
              {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Plus aria-hidden="true" className="h-4 w-4" />}Add to cart
            </button>
          )}
          {item.kind === 'reorder' && (
            <Link to={dtpPath(physicianId, '/orders')} onClick={() => track('auto_refill_setup_opened', { placement: 'recommendation' })} className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white">
              <Repeat aria-hidden="true" className="h-4 w-4" />Set up auto-refill
            </Link>
          )}
        </div>
      )}
      {message && <p role="alert" className="mt-2 text-sm text-[#8c2a14]">{message}</p>}
    </article>
  );
}
